function getFocusableElements (container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  )
}

// eslint-disable-next-line no-unused-vars
class SectionId {
  static #separator = '__'

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId (qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0]
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName (qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1]
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection (sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`
  }
}

// eslint-disable-next-line no-unused-vars
class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition (oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent))

    const newNodeWrapper = document.createElement('div')
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
    const newNode = newNodeWrapper.firstChild

    // dedupe IDs
    const uniqueKey = Date.now()
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`)
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
    })

    oldNode.parentNode.insertBefore(newNode, oldNode)
    oldNode.style.display = 'none'

    postProcessCallbacks?.forEach((callback) => callback(newNode))

    setTimeout(() => oldNode.remove(), 500)
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML (element, html) {
    element.innerHTML = html
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script')
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      })
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
    })
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button')
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'))

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id)
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
  })

  if (summary.closest('header-drawer, menu-drawer')) return
  summary.parentElement.addEventListener('keyup', onKeyUpEscape)
})

const trapFocusHandlers = {}

function trapFocus (container, elementToFocus = container) {
  const elements = getFocusableElements(container)
  const first = elements[0]
  const last = elements[elements.length - 1]

  removeTrapFocus()

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return

    document.addEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener('focusout', trapFocusHandlers.focusout)
  document.addEventListener('focusin', trapFocusHandlers.focusin)

  elementToFocus.focus()

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length)
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible')
} catch (e) {
  focusVisiblePolyfill()
}

function focusVisiblePolyfill () {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN'
  ]
  let currentFocusedElement = null
  let mouseClick = null

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false
    }
  })

  window.addEventListener('mousedown', (event) => {
    mouseClick = true
  })

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused')

      if (mouseClick) return

      currentFocusedElement = document.activeElement
      currentFocusedElement.classList.add('focused')
    },
    true
  )
}

// eslint-disable-next-line no-unused-vars
function pauseAllMedia () {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
  })
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*')
  })
  document.querySelectorAll('video').forEach((video) => video.pause())
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause()
  })
}

function removeTrapFocus (elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin)
  document.removeEventListener('focusout', trapFocusHandlers.focusout)
  document.removeEventListener('keydown', trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape (event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return

  const openDetailsElement = event.target.closest('details[open]')
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector('summary')
  openDetailsElement.removeAttribute('open')
  summaryElement.setAttribute('aria-expanded', false)
  summaryElement.focus()
}

const isProductPage = document.body.classList.contains('product')
// eslint-disable-next-line no-undef
if (!customElements.get('quantity-input') && !isProductPage) {
  // eslint-disable-next-line no-undef
  class QuantityInput extends HTMLElement {
    constructor () {
      super()
      this.input = this.querySelector('input')
      this.changeEvent = new Event('change', { bubbles: true })
      this.input.addEventListener('change', this.onInputChange.bind(this))
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      )
    }

    quantityUpdateUnsubscriber = undefined

    connectedCallback () {
      this.validateQtyRules()
      // eslint-disable-next-line no-undef
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))
    }

    disconnectedCallback () {
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber()
      }
    }

    onInputChange (event) {
      this.validateQtyRules()
    }

    onButtonClick (event) {
      event.preventDefault()
      const previousValue = this.input.value

      if (event.target.name === 'plus') {
        if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value === 0) {
          this.input.value = this.input.dataset.min
        } else {
          this.input.stepUp()
        }
      } else {
        this.input.stepDown()
      }

      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

      if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
        this.input.value = parseInt(this.input.min)
      }
    }

    validateQtyRules () {
      const value = parseInt(this.input.value)
      if (this.input.min) {
        const buttonMinus = this.querySelector(".quantity__button[name='minus']")
        buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
      }
      if (this.input.max) {
        const max = parseInt(this.input.max)
        const buttonPlus = this.querySelector(".quantity__button[name='plus']")
        buttonPlus.classList.toggle('disabled', value >= max)
      }
    }
  }
  // eslint-disable-next-line no-undef
  customElements.define('quantity-input', QuantityInput)
}

// eslint-disable-next-line no-unused-vars
function debounce (fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

// eslint-disable-next-line no-unused-vars
function throttle (fn, delay) {
  let lastCall = 0
  return function (...args) {
    const now = new Date().getTime()
    if (now - lastCall < delay) {
      return
    }
    lastCall = now
    return fn(...args)
  }
}

// eslint-disable-next-line no-unused-vars
function fetchConfig (type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  }
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify === 'undefined') {
  window.Shopify = {}
}

// eslint-disable-next-line no-undef
Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments)
  }
}

// eslint-disable-next-line no-undef
Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0, count = selector.options.length; i < count; i++) {
    const option = selector.options[i]
    if (value === option.value || value === option.innerHTML) {
      selector.selectedIndex = i
      return i
    }
  }
}

// eslint-disable-next-line no-undef
Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback)
}

// eslint-disable-next-line no-undef
Shopify.postLink = function (path, options) {
  options = options || {}
  const method = options.method || 'post'
  const params = options.parameters || {}

  const form = document.createElement('form')
  form.setAttribute('method', method)
  form.setAttribute('action', path)

  for (const key in params) {
    const hiddenField = document.createElement('input')
    hiddenField.setAttribute('type', 'hidden')
    hiddenField.setAttribute('name', key)
    hiddenField.setAttribute('value', params[key])
    form.appendChild(hiddenField)
  }
  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

// eslint-disable-next-line no-undef
Shopify.CountryProvinceSelector = function (countryDomId, provinceDomId, options) {
  this.countryEl = document.getElementById(countryDomId)
  this.provinceEl = document.getElementById(provinceDomId)
  this.provinceContainer = document.getElementById(options.hideElement || provinceDomId)

  // eslint-disable-next-line no-undef
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this))

  this.initCountry()
  this.initProvince()
}

// eslint-disable-next-line no-undef
Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute('data-default')
    // eslint-disable-next-line no-undef
    Shopify.setSelectorByValue(this.countryEl, value)
    this.countryHandler()
  },

  initProvince: function () {
    const value = this.provinceEl.getAttribute('data-default')
    if (value && this.provinceEl.options.length > 0) {
      // eslint-disable-next-line no-undef
      Shopify.setSelectorByValue(this.provinceEl, value)
    }
  },

  countryHandler: function (e) {
    let opt = this.countryEl.options[this.countryEl.selectedIndex]
    const raw = opt.getAttribute('data-provinces')
    const provinces = JSON.parse(raw)

    this.clearOptions(this.provinceEl)
    if (provinces && provinces.length === 0) {
      this.provinceContainer.style.display = 'none'
    } else {
      for (let i = 0; i < provinces.length; i++) {
        opt = document.createElement('option')
        opt.value = provinces[i][0]
        opt.innerHTML = provinces[i][1]
        this.provinceEl.appendChild(opt)
      }

      this.provinceContainer.style.display = ''
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild)
    }
  },

  setOptions: function (selector, values) {
    for (let i = 0; i < values.length; i++) {
      const opt = document.createElement('option')
      opt.value = values[i]
      opt.innerHTML = values[i]
      selector.appendChild(opt)
    }
  }
}

// eslint-disable-next-line no-undef
class MenuDrawer extends HTMLElement {
  constructor () {
    super()

    this.mainDetailsToggle = this.querySelector('details')

    this.addEventListener('keyup', this.onKeyUp.bind(this))
    this.addEventListener('focusout', this.onFocusOut.bind(this))
    this.bindEvents()
  }

  bindEvents () {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    )
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)))
  }

  onKeyUp (event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return

    const openDetailsElement = event.target.closest('details[open]')
    if (!openDetailsElement) return

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement)
  }

  onSummaryClick (event) {
    const summaryElement = event.currentTarget
    const detailsElement = summaryElement.parentNode
    const parentMenuElement = detailsElement.closest('.has-submenu')
    const isOpen = detailsElement.hasAttribute('open')
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    function addTrapFocus () {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'))
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus)
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault()
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement)

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`)
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening')
        summaryElement.setAttribute('aria-expanded', true)
        parentMenuElement && parentMenuElement.classList.add('submenu-open')
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus)
      }, 100)
    }
  }

  openMenuDrawer (summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening')
    })
    summaryElement.setAttribute('aria-expanded', true)
    trapFocus(this.mainDetailsToggle, summaryElement)
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`)
  }

  closeMenuDrawer (event, elementToFocus = false) {
    if (event === undefined) return

    this.mainDetailsToggle.classList.remove('menu-opening')
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open')
      details.classList.remove('menu-opening')
      // Reset aria-expanded on summary elements
      const summary = details.querySelector('summary')
      if (summary) summary.setAttribute('aria-expanded', 'false')
    })
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open')
    })

    // Reset scroll position of all submenus to top
    this.mainDetailsToggle.querySelectorAll('.menu-drawer__submenu').forEach((submenu) => {
      submenu.scrollTop = 0
    })

    // Reset scroll position of main menu drawer
    const menuDrawer = this.querySelector('#menu-drawer')
    if (menuDrawer) {
      menuDrawer.scrollTop = 0
    }

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`)
    removeTrapFocus(elementToFocus)
    this.closeAnimation(this.mainDetailsToggle)

    // Set aria-expanded to false on the main summary element
    const mainSummary = this.mainDetailsToggle.querySelector('summary')
    if (mainSummary) mainSummary.setAttribute('aria-expanded', 'false')

    // eslint-disable-next-line no-undef
    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false)
  }

  onFocusOut () {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) { this.closeMenuDrawer() }
    })
  }

  onCloseButtonClick (event) {
    if (event.currentTarget.id === 'close-menu-drawer') {
      // Close the entire menu drawer when the main close button is clicked
      // Immediately remove open attribute to hide overlay
      if (this.mainDetailsToggle) {
        this.mainDetailsToggle.removeAttribute('open')
      }
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      return
    }
    // Otherwise, just close the submenu
    const detailsElement = event.currentTarget.closest('details')
    this.closeSubmenu(detailsElement)
  }

  closeSubmenu (detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open')
    parentMenuElement && parentMenuElement.classList.remove('submenu-open')
    detailsElement.classList.remove('menu-opening')
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false)
    removeTrapFocus(detailsElement.querySelector('summary'))

    // Reset scroll position of the submenu being closed
    const submenu = detailsElement.querySelector('.menu-drawer__submenu')
    if (submenu) {
      submenu.scrollTop = 0
    }

    this.closeAnimation(detailsElement)
  }

  closeAnimation (detailsElement) {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      const elapsedTime = time - animationStart

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation)
      } else {
        detailsElement.removeAttribute('open')
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'))
        }
      }
    }

    window.requestAnimationFrame(handleAnimation)
  }
}

// eslint-disable-next-line no-undef
customElements.define('menu-drawer', MenuDrawer)

class HeaderDrawer extends MenuDrawer {
  openMenuDrawer (summaryElement) {
    this.header = this.header || document.querySelector('.section-header')
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    )
    this.header.classList.add('menu-open')

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening')
    })

    summaryElement.setAttribute('aria-expanded', true)
    window.addEventListener('resize', this.onResize)
    trapFocus(this.mainDetailsToggle, summaryElement)
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`)
  }

  closeMenuDrawer (event, elementToFocus) {
    if (!elementToFocus) return
    super.closeMenuDrawer(event, elementToFocus)
    this.header.classList.remove('menu-open')
    window.removeEventListener('resize', this.onResize)
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      )
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`)
  }
}

// eslint-disable-next-line no-undef
customElements.define('header-drawer', HeaderDrawer)

// eslint-disable-next-line no-undef
class ModalDialog extends HTMLElement {
  constructor () {
    super()
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false))
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide()
    })
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide()
      })
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide()
      })
    }
  }

  connectedCallback () {
    if (this.moved) return
    this.moved = true
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '')
    document.body.appendChild(this)
  }

  show (opener) {
    this.openedBy = opener
    const popup = this.querySelector('.template-popup')
    document.body.classList.add('overflow-hidden')
    this.setAttribute('open', '')
    if (popup) popup.loadContent()
    trapFocus(this, this.querySelector('[role="dialog"]'))
    window.pauseAllMedia()
  }

  hide () {
    document.body.classList.remove('overflow-hidden')
    document.body.dispatchEvent(new CustomEvent('modalClosed'))
    this.removeAttribute('open')
    removeTrapFocus(this.openedBy)
    window.pauseAllMedia()
  }
}
// eslint-disable-next-line no-undef
customElements.define('modal-dialog', ModalDialog)

// eslint-disable-next-line no-undef
class BulkModal extends HTMLElement {
  connectedCallback () {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return
      observer.unobserve(this)
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0]
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            // eslint-disable-next-line no-undef
            const html = new DOMParser().parseFromString(responseText, 'text/html')
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode
            this.innerHTML = sourceQty.innerHTML
          })
          .catch((e) => {
            // Error fetching bulk modal content
          })
      }
    }

    // eslint-disable-next-line no-undef
    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    )
  }
}

// eslint-disable-next-line no-undef
customElements.define('bulk-modal', BulkModal)

// eslint-disable-next-line no-undef
class ModalOpener extends HTMLElement {
  constructor () {
    super()

    const button = this.querySelector('button')

    if (!button) return
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'))
      if (modal) modal.show(button)
    })
  }
}
// eslint-disable-next-line no-undef
customElements.define('modal-opener', ModalOpener)

// eslint-disable-next-line no-undef
class DeferredMedia extends HTMLElement {
  constructor () {
    super()
    const poster = this.querySelector('[id^="Deferred-Poster-"]')
    if (!poster) return
    poster.addEventListener('click', this.loadContent.bind(this))
  }

  loadContent (focus = true) {
    window.pauseAllMedia()
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div')
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true))

      this.setAttribute('loaded', true)
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'))
      if (focus) deferredElement.focus()
      if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play()
      } else if (deferredElement.nodeName === 'IFRAME' && deferredElement.classList.contains('js-youtube')) {
        if (window.YT) {
          // eslint-disable-next-line no-new
          new window.YT.Player(deferredElement.id, {
            events: {
              onReady: event => {
                event.target.playVideo()
              }
            }
          })
        }
        // deferredElement.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*')
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style')
      deferredElement.setAttribute('style', 'display: block;')
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle)
      }, 0)
    }
  }
}

// eslint-disable-next-line no-undef
customElements.define('deferred-media', DeferredMedia)

// eslint-disable-next-line no-undef
class SliderComponent extends HTMLElement {
  constructor () {
    super()
    this.slider = this.querySelector('[id^="Slider-"]')
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
    this.enableSliderLooping = false
    this.currentPageElement = this.querySelector('.slider-counter--current')
    this.pageTotalElement = this.querySelector('.slider-counter--total')
    this.prevButton = this.querySelector('button[name="previous"]')
    this.nextButton = this.querySelector('button[name="next"]')

    if (!this.slider || !this.nextButton) return

    this.initPages()
    // eslint-disable-next-line no-undef
    const resizeObserver = new ResizeObserver((entries) => this.initPages())
    resizeObserver.observe(this.slider)

    this.slider.addEventListener('scroll', this.update.bind(this))
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this))
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this))
  }

  initPages () {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0)
    if (this.sliderItemsToShow.length < 2) return
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    )
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1
    this.update()
  }

  resetPages () {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
    this.initPages()
  }

  update () {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return

    const previousPage = this.currentPage
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage
      this.pageTotalElement.textContent = this.totalPages
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1]
          }
        })
      )
    }

    if (this.enableSliderLooping) return

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled')
    } else {
      this.prevButton.removeAttribute('disabled')
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled')
    } else {
      this.nextButton.removeAttribute('disabled')
    }
  }

  isSlideVisible (element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft
  }

  onButtonClick (event) {
    event.preventDefault()
    const step = event.currentTarget.dataset.step || 1
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset
    this.setSlidePosition(this.slideScrollPosition)
  }

  setSlidePosition (position) {
    this.slider.scrollTo({
      left: position
    })
  }
}
// eslint-disable-next-line no-undef
customElements.define('slider-component', SliderComponent)

class SlideshowComponent extends SliderComponent {
  constructor () {
    super()
    this.sliderControlWrapper = this.querySelector('.slider-buttons')
    this.enableSliderLooping = true

    if (!this.sliderControlWrapper) return

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide')
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider')
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'))
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)))
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this))
    this.setSlideVisibility()

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay()
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true
          },
          { once: true }
        )
      })
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay()
  }

  setAutoPlay () {
    this.autoplaySpeed = this.slider.dataset.speed * 1000
    this.addEventListener('mouseover', this.focusInHandling.bind(this))
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this))
    this.addEventListener('focusin', this.focusInHandling.bind(this))
    this.addEventListener('focusout', this.focusOutHandling.bind(this))

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay')
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this))
      this.autoplayButtonIsSetToPlay = true
      this.play()
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play()
    }
  }

  onButtonClick (event) {
    super.onButtonClick(event)
    this.wasClicked = true

    const isFirstSlide = this.currentPage === 1
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name)
      return
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0
    }

    this.setSlidePosition(this.slideScrollPosition)

    this.applyAnimationToAnnouncementBar(event.currentTarget.name)
  }

  setSlidePosition (position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout)
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position
      })
    }, this.announcerBarAnimationDelay)
  }

  update () {
    super.update()
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link')
    this.prevButton.removeAttribute('disabled')

    if (!this.sliderControlButtons.length) return

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active')
      link.removeAttribute('aria-current')
    })
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active')
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true)
  }

  autoPlayToggle () {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay)
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play()
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay
  }

  focusOutHandling (event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target)
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return
      this.play()
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play()
    }
  }

  focusInHandling (event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target)
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play()
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause()
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause()
    }
  }

  play () {
    this.slider.setAttribute('aria-live', 'off')
    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed)
  }

  pause () {
    this.slider.setAttribute('aria-live', 'polite')
    clearInterval(this.autoplay)
  }

  togglePlayButtonState (pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused')
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow)
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused')
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow)
    }
  }

  autoRotateSlides () {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset

    this.setSlidePosition(slideScrollPosition)
    this.applyAnimationToAnnouncementBar()
  }

  setSlideVisibility (event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a')
      if (index === this.currentPage - 1) {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex')
          })
        }
        item.setAttribute('aria-hidden', 'false')
        item.removeAttribute('tabindex')
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1')
          })
        }
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })
    this.wasClicked = false
  }

  applyAnimationToAnnouncementBar (button = 'next') {
    if (!this.announcementBarSlider) return

    const itemsCount = this.sliderItems.length
    const increment = button === 'next' ? 1 : -1

    const currentIndex = this.currentPage - 1
    let nextIndex = (currentIndex + increment) % itemsCount
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex

    const nextSlide = this.sliderItems[nextIndex]
    const currentSlide = this.sliderItems[currentIndex]

    const animationClassIn = 'announcement-bar-slider--fade-in'
    const animationClassOut = 'announcement-bar-slider--fade-out'

    const isFirstSlide = currentIndex === 0
    const isLastSlide = currentIndex === itemsCount - 1

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide)
    const direction = shouldMoveNext ? 'next' : 'previous'

    currentSlide.classList.add(`${animationClassOut}-${direction}`)
    nextSlide.classList.add(`${animationClassIn}-${direction}`)

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`)
      nextSlide.classList.remove(`${animationClassIn}-${direction}`)
    }, this.announcerBarAnimationDelay * 2)
  }

  linkToSlide (event) {
    event.preventDefault()
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage)
    this.slider.scrollTo({
      left: slideScrollPosition
    })
  }
}

// eslint-disable-next-line no-undef
customElements.define('slideshow-component', SlideshowComponent)

// eslint-disable-next-line no-undef
class VariantSelects extends HTMLElement {
  static variantChangedListenerAdded = false
  static syncInProgress = false

  connectedCallback () {
    if (this.initialized) {
      return
    }
    this.initialized = true

    this.addUnavailableStyles()

    this.loadVariants()

    this.changeHandler = this.onVariantChange.bind(this)
    this.addEventListener('change', this.changeHandler)

    this.updateOptions()
    this.updateMasterId()

    // Filter Pack Size options on initial load
    setTimeout(() => {
      this.filterOptions()
    }, 0)

    this.setAvailability()

    if (!window.productVariantData) {
      window.productVariantData = {}
    }

    this.updateAvailableOptions()

    this.preventDisabledClicks()
  }

  preventDisabledClicks () {
    // For Sleep Doctor custom selects, prevent clicks on disabled options
    this.addEventListener('click', (e) => {
      const clickedOption = e.target.closest('.selectOption.disabled, .selectOption.unavailable')
      if (clickedOption) {
        e.stopPropagation()
        e.preventDefault()
      }
    }, true)
  }

  isOptionChangeRequiringFiltering (element) {
    // Check if the changed element requires filtering (Size or Style options)
    return (
      (element.type === 'radio' && element.name && (element.name.includes('Size') || element.name.includes('Style'))) ||
      (element.tagName === 'SELECT' && element.name && (element.name.includes('Size') || element.name.includes('Style'))) ||
      (element.tagName === 'SELECT' && element.id && element.id.includes('-0'))
    )
  }

  getCurrentSize () {
    // Get currently selected Size from radio buttons or selects
    const sizeRadio = this.querySelector('input[type="radio"]:checked')
    if (sizeRadio) {
      return sizeRadio.value
    }

    const sizeSelect = this.querySelector('select[name*="Size"]') || this.querySelector('select[id*="-0"]')
    if (sizeSelect) {
      return sizeSelect.value
    }

    return null
  }

  getCurrentStyle () {
    // Get currently selected Style from radio buttons or selects
    const styleRadio = this.querySelector('input[type="radio"]:checked')
    if (styleRadio) {
      return styleRadio.value
    }

    const styleSelect = this.querySelector('select[name*="Style"]') || this.querySelector('select[id*="-0"]')
    if (styleSelect) {
      return styleSelect.value
    }

    return null
  }

  getPackSizeSelect () {
    // Find Pack Size dropdown - could be in current element or in sticky section
    return this.querySelector('select[name*="Pack"]') ||
           this.querySelector('select[id*="-1"]') ||
           document.querySelector('.sd-sticky-button select[name*="Pack"]')
  }

  getPackSizeRadioButtons () {
    // Find Pack Size radio buttons in the main product section
    const radios = this.querySelectorAll('input[type="radio"][name*="Pack"]')
    return radios
  }

  detectOptionDependencies () {
    // Analyze variants to automatically detect option dependencies
    const dependencies = {}

    // Group variants by option1 values to analyze option2 restrictions
    const optionMap = new Map()

    this.variants.forEach(variant => {
      if (!variant.option1 || !variant.option2) return

      if (!optionMap.has(variant.option1)) {
        optionMap.set(variant.option1, new Set())
      }
      optionMap.get(variant.option1).add(variant.option2)
    })

    // Check if any option1 value restricts option2 choices
    const allOption2Values = new Set()
    this.variants.forEach(variant => {
      if (variant.option2) allOption2Values.add(variant.option2)
    })

    optionMap.forEach((option2Values, option1Value) => {
      // If this option1 value has fewer option2 choices than exist overall,
      // then there's a dependency
      if (option2Values.size < allOption2Values.size) {
        dependencies[option1Value] = Array.from(option2Values)
      }
    })

    return dependencies
  }

  applyOptionFiltering (dependencies) {
    // Get current selection for option1
    const currentOption1 = this.getCurrentOption1()

    if (!currentOption1) {
      return
    }

    if (!dependencies[currentOption1]) {
      // No dependency means all option2 values should be available
      const allOption2Values = []
      this.variants.forEach(variant => {
        if (variant.option2 && !allOption2Values.includes(variant.option2)) {
          allOption2Values.push(variant.option2)
        }
      })

      const firstAvailable = this.getFirstAvailableOption2(allOption2Values)

      // Apply filtering to restore all options
      this.filterOption2Controls(allOption2Values, firstAvailable)
      return
    }

    const validOption2Values = dependencies[currentOption1]
    const firstAvailable = this.getFirstAvailableOption2(validOption2Values)

    // Apply filtering to option2 controls
    this.filterOption2Controls(validOption2Values, firstAvailable)
  }

  getCurrentOption1 () {
    // Get currently selected option1 value (could be Style, Size, etc.)
    const option1Radio = this.querySelector('input[type="radio"][name*="options[0]"]:checked') ||
                        this.querySelector('input[type="radio"]:checked')
    if (option1Radio) {
      return option1Radio.value
    }

    const option1Select = this.querySelector('select[name*="options[0]"]') ||
                         this.querySelector('select[id*="-0"]')
    if (option1Select) {
      return option1Select.value
    }

    return null
  }

  getFirstAvailableOption2 (validValues) {
    // Find the first available option2 value from the valid set
    for (const value of validValues) {
      const hasAvailableVariant = this.variants.some(variant =>
        variant.option2 === value && variant.available
      )
      if (hasAvailableVariant) {
        return value
      }
    }
    return validValues[0] // Fallback to first valid even if not available
  }

  filterOption2Controls (validValues, firstAvailable) {
    // Try multiple selectors to find option2 radio buttons
    let option2Radios = this.querySelectorAll('input[type="radio"][name*="options[1]"]')

    if (option2Radios.length === 0) {
      // Be more specific - look for Pack Size, not just Size (to avoid Size-1 which is option1)
      option2Radios = this.querySelectorAll('input[type="radio"][name*="Pack Size"], input[type="radio"][name*="Pack-"]')
    }

    if (option2Radios.length === 0) {
      // For Fisher & Paykel style products, Size-2 is option2
      option2Radios = this.querySelectorAll('input[type="radio"][name="Size-2"]')
    }

    if (option2Radios.length === 0) {
      // Try generic -2 ending pattern
      option2Radios = this.querySelectorAll('input[type="radio"][name$="-2"]')
    }

    if (option2Radios.length === 0) {
      // Manual exclusion: find option1 first, then exclude
      const allRadios = this.querySelectorAll('input[type="radio"]')
      const option1Radios = this.querySelectorAll('input[type="radio"][name*="Style"], input[type="radio"][name$="-1"]')

      option2Radios = Array.from(allRadios).filter(radio => !Array.from(option1Radios).includes(radio))
    }

    if (option2Radios.length > 0) {
      this.filterRadioButtons(option2Radios, validValues, firstAvailable)
    }

    // Try multiple selectors to find option2 select dropdowns
    let option2Selects = this.querySelectorAll('select[name*="options[1]"]')

    if (option2Selects.length === 0) {
      // Try exact Pack Size selector
      option2Selects = this.querySelectorAll('select[name="options[Pack Size]"]')
    }

    if (option2Selects.length === 0) {
      // For Fisher & Paykel: if there's no Pack Size dropdown, Size is option2
      const hasPackSizeDropdown = this.querySelector('select[name="options[Pack Size]"]')
      if (!hasPackSizeDropdown) {
        option2Selects = this.querySelectorAll('select[name="options[Size]"]')
      }
    }

    if (option2Selects.length === 0) {
      option2Selects = this.querySelectorAll('select[id*="-1"]')
    }

    if (option2Selects.length === 0) {
      // Manual exclusion: find option1 first, then exclude
      const allSelects = this.querySelectorAll('select')
      const option1Selects = this.querySelectorAll('select[name="options[Style]"], select[name*="Style"], select[name="options[Size]"]')

      option2Selects = Array.from(allSelects).filter(select => !Array.from(option1Selects).includes(select))
    }

    if (option2Selects.length > 0) {
      this.filterSelectDropdowns(option2Selects, validValues, firstAvailable)
    }
  }

  filterRadioButtons (radios, validValues, firstAvailable) {
    let needsNewSelection = false
    let currentSelectionIsValid = false
    let hasCurrentSelection = false

    // Ensure containing fieldset is visible
    const fieldset = radios[0]?.closest('fieldset')
    if (fieldset) {
      fieldset.style.display = ''
    }

    radios.forEach(radio => {
      const label = this.querySelector(`label[for="${radio.id}"]`)

      if (radio.checked) {
        hasCurrentSelection = true
        currentSelectionIsValid = validValues.includes(radio.value)
        if (!currentSelectionIsValid) {
          radio.checked = false
          needsNewSelection = true
        }
      }

      // Show all options (don't hide any for radio buttons)
      radio.style.display = ''
      if (label) label.style.display = ''

      // Set disabled state based on whether this option is in validValues
      const isValidOption = validValues.includes(radio.value)
      radio.disabled = !isValidOption

      // Also update label classes for visual feedback
      if (label) {
        if (isValidOption) {
          label.classList.remove('disabled', 'unavailable')
        } else {
          label.classList.add('disabled', 'unavailable')
        }
      }
    })

    // Auto-select first available if no selection or invalid selection
    if ((!hasCurrentSelection || needsNewSelection || !currentSelectionIsValid) && firstAvailable) {
      // Clear ONLY the option2 selections (not option1 like Style)
      radios.forEach(radio => { radio.checked = false })

      const firstRadio = Array.from(radios).find(radio => radio.value === firstAvailable)
      if (firstRadio) {
        firstRadio.checked = true
        // Trigger change event to update variant
        firstRadio.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }
  }

  filterSelectDropdowns (selects, validValues, firstAvailable) {
    selects.forEach(select => {
      // Store clean options if not already stored
      if (!select._allOptions || select._allOptions.some(opt => opt.text.includes('Unavailable'))) {
        select._allOptions = Array.from(select.options).map(option => {
          const cleanText = option.textContent.replace(/\s*-\s*(Unavailable|Out of stock).*$/i, '').trim()
          const cleanOption = option.cloneNode(true)
          cleanOption.textContent = cleanText
          cleanOption.removeAttribute('disabled')
          return {
            value: option.value,
            text: cleanText,
            element: cleanOption
          }
        })
      }

      // Check if rebuild is needed
      const currentOptions = Array.from(select.options).map(o => o.value).sort()
      const newOptions = validValues.slice().sort()
      const optionsChanged = JSON.stringify(currentOptions) !== JSON.stringify(newOptions)

      if (optionsChanged) {
        // Rebuild dropdown with valid options only
        const currentValue = select.value
        select.innerHTML = ''

        select._allOptions.forEach(optionData => {
          if (validValues.includes(optionData.value)) {
            select.appendChild(optionData.element.cloneNode(true))
          }
        })

        // Set appropriate value
        const currentValueValid = validValues.includes(currentValue)
        if (currentValueValid) {
          select.value = currentValue
        } else if (firstAvailable) {
          select.value = firstAvailable
          // Trigger change to update variant
          select.dispatchEvent(new Event('change', { bubbles: true }))
        }

        // Update availability after rebuilding
        setTimeout(() => {
          if (!VariantSelects.syncInProgress) {
            this.updateOptions()
            this.updateMasterId()
            this.setAvailability()
          }
        }, 0)
      }
    })
  }

  filterOptions () {
    // Prevent infinite loops
    if (this._filteringInProgress) return
    this._filteringInProgress = true

    // Detect all option dependencies automatically
    const dependencies = this.detectOptionDependencies()

    if (Object.keys(dependencies).length === 0) {
      this._filteringInProgress = false
      return
    }

    // Apply filtering for each detected dependency
    this.applyOptionFiltering(dependencies)

    this._filteringInProgress = false

    // After filtering is complete, sync the selection to other variant-selects elements
    // But only if this filtering was NOT triggered by a sync operation
    if (!VariantSelects.syncInProgress && !this._filteringTriggeredBySync) {
      this.updateOptions()
      this.updateMasterId()
      this.setAvailability()
      this.syncOtherVariantSelects()
    }
  }

  updateProductInfoAvailability () {
    // Update the price and availability display in product-info components
    if (!this.currentVariant) return

    const productInfo = this.closest('product-info')
    if (!productInfo) return

    // Update price display
    const priceElement = productInfo.querySelector(`#price-${productInfo.dataset.section}`)
    if (priceElement) {
      if (!this.currentVariant.available) {
        const soldOutElement = priceElement.querySelector('.price__availability')
        if (soldOutElement) {
          soldOutElement.classList.remove('hidden')
        }
      } else {
        const soldOutElement = priceElement.querySelector('.price__availability')
        if (soldOutElement) {
          soldOutElement.classList.add('hidden')
        }
      }
    }
  }

  addUnavailableStyles () {
    // Add styles only once
    if (!document.getElementById('variant-unavailable-styles')) {
      const style = document.createElement('style')
      style.id = 'variant-unavailable-styles'
      style.textContent = `
        /* Unavailable radio buttons and labels - using existing .disabled class */
        input[type="radio"]:disabled + label,
        input[type="radio"].disabled + label,
        label.unavailable,
        label.disabled {
          opacity: 0.5;
          cursor: not-allowed !important;
          text-decoration: line-through;
          pointer-events: none;
        }
        
        /* Remove cursor pointer from parent elements */
        .product-form__input--pill:has(input[type="radio"]:disabled),
        .product-form__input--pill:has(input[type="radio"].disabled) {
          cursor: default !important;
        }
        
        /* Unavailable select options */
        select option:disabled {
          color: #999;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        /* Style disabled options in webkit browsers */
        select option:disabled:checked {
          color: #999;
          background-color: #f5f5f5;
        }
        
        /* Unavailable custom select options */
        .selectOption.unavailable,
        .selectOption.disabled {
          opacity: 0.5;
          cursor: not-allowed !important;
          text-decoration: line-through;
          pointer-events: none;
        }
        
        /* Remove pointer cursor from swatch inputs */
        .product-form__input--swatch input:disabled + label,
        .product-form__input--swatch input.disabled + label {
          cursor: not-allowed !important;
        }
      `
      document.head.appendChild(style)
    }
  }

  loadVariants (forceReload = false) {
    if (!window.productVariantData) window.productVariantData = {}

    const productForm = document.querySelector(`#product-form-${this.dataset.section}`)
    const productId = productForm?.dataset?.productId || this.closest('[data-product-id]')?.dataset?.productId

    if (productId && window.productVariantData[productId] && !forceReload) {
      this.variants = window.productVariantData[productId]
      return
    }
    let variantData = null

    // Prioritize element-local variant data to avoid picking up wrong product data
    const selectedVariantScript = this.querySelector('script[type="application/json"][data-selected-variant]')
    const hasDawnPicker = !!selectedVariantScript

    if (hasDawnPicker) {
      const localVariantsScript = this.querySelector('script[type="application/json"]:not([data-resource]):not([data-selected-variant])')
      if (localVariantsScript) {
        try {
          const allVariants = JSON.parse(localVariantsScript.textContent)
          if (Array.isArray(allVariants)) {
            variantData = allVariants
          }
        } catch (e) {
        }
      }
    }

    if (!hasDawnPicker) {
      const variantsScript = this.querySelector('script[type="application/json"]:not([data-selected-variant])')
      if (variantsScript) {
        try {
          const parsedData = JSON.parse(variantsScript.textContent)

          if (Array.isArray(parsedData)) {
            variantData = parsedData
          } else if (parsedData && parsedData.variants && Array.isArray(parsedData.variants)) {
            variantData = parsedData.variants
          }
        } catch (e) {
        }
      }
    }

    // Fall back to document-level search if no local data found
    if (!variantData) {
      const productJsonSelectors = [
        `script#ProductJson-${this.dataset.section}`,
        'script#ProductJson-product-template',
        'script[data-product-json]',
        '.product__info script[type="application/json"]:not([data-resource])',
        '#shopify-product-json',
        'script[type="application/ld+json"]'
      ]

      for (const selector of productJsonSelectors) {
        const productScript = document.querySelector(selector)
        if (productScript) {
          try {
            const productData = JSON.parse(productScript.textContent)

            if (selector.includes('ld+json') && productData['@type'] === 'Product') {
              continue
            }

            if (productData.product && productData.product.variants) {
              variantData = productData.product.variants
              break
            } else if (productData.variants && Array.isArray(productData.variants)) {
              variantData = productData.variants
              break
            }
          } catch (e) {
          }
        }
      }
    }

    if (!variantData) {
      const otherVariantSelects = document.querySelectorAll('variant-selects')
      for (const vs of otherVariantSelects) {
        if (vs !== this && vs.variants && Array.isArray(vs.variants) && vs.variants.length > 1) {
          variantData = [...vs.variants]
          // loadMethod = 'Copied from other variant-selects'
          break
        }
      }
    }

    if (!variantData) {
      const thisVariantsScript = this.querySelector('script[type="application/json"]:not([data-resource]):not([data-selected-variant])')
      if (thisVariantsScript) {
        try {
          const allVariants = JSON.parse(thisVariantsScript.textContent)
          if (Array.isArray(allVariants)) {
            variantData = allVariants
          }
        } catch (e) {
        }
      }

      if (!variantData) {
        const allVariantsScript = document.querySelector('variant-selects script[type="application/json"]:not([data-resource]):not([data-selected-variant])')
        if (allVariantsScript) {
          try {
            const allVariants = JSON.parse(allVariantsScript.textContent)
            if (Array.isArray(allVariants)) {
              variantData = allVariants
            }
          } catch (e) {
          }
        }
      }

      if (!variantData) {
        const variantRadios = document.querySelector('variant-radios script[type="application/json"]:not([data-resource])')
        if (variantRadios) {
          try {
            const allVariants = JSON.parse(variantRadios.textContent)
            if (Array.isArray(allVariants)) {
              variantData = allVariants
            }
          } catch (e) {
          }
        }
      }
    }

    if (!variantData) {
      const productInfo = document.querySelector(`product-info[data-section="${this.dataset.section}"]`)
      if (productInfo) {
        const productScript = productInfo.querySelector('script[type="application/json"]')
        if (productScript) {
          try {
            const productData = JSON.parse(productScript.textContent)
            if (productData.variants) {
              variantData = productData.variants
              // loadMethod = 'Product info element'
            }
          } catch (e) {
            // Error: Console error was here
          }
        }
      }
    }

    if (!variantData) {
      const pageScripts = document.querySelectorAll('script')
      for (const script of pageScripts) {
        if (script.textContent.includes('product.variants') || script.textContent.includes('"variants":')) {
          // More robust regex to capture variants array including nested objects
          const match = script.textContent.match(/"variants":\s*(\[[\s\S]*?\])\s*[,}]/)
          if (match) {
            try {
              variantData = JSON.parse(match[1])
              // loadMethod = 'Page script (variants JSON)'
              break
            } catch (e) {
              // Try alternative pattern for product.variants assignment
              const altMatch = script.textContent.match(/product\.variants\s*=\s*(\[[\s\S]*?\]);/)
              if (altMatch) {
                try {
                  variantData = JSON.parse(altMatch[1])
                  // loadMethod = 'Page script (product.variants)'
                  break
                } catch (e2) {
                  // Continue searching
                }
              }
            }
          }
        }
      }
    }

    if (!variantData) {
      // Sometimes Shopify themes store variants directly on the element
      if (this.dataset.variants) {
        try {
          variantData = JSON.parse(this.dataset.variants)
          // loadMethod = 'Data attribute'
        } catch (e) {
          // Error: Console error was here
        }
      }
    }

    if (!variantData) {
      const productInfoParent = this.closest('product-info')
      if (productInfoParent) {
        const parentScript = productInfoParent.querySelector('script[type="application/json"]')
        if (parentScript) {
          try {
            const parentData = JSON.parse(parentScript.textContent)
            if (parentData.variants) {
              variantData = parentData.variants
              // loadMethod = 'Parent product-info element'
            }
          } catch (e) {
            // Error: Console error was here
          }
        }
      }
    }

    // Set the variants
    if (variantData && Array.isArray(variantData)) {
      this.variants = variantData
      this._originalVariants = [...variantData]

      // Store globally for sharing between variant-selects
      if (productId) {
        window.productVariantData[productId] = variantData
      }
    } else {
      // Error: Console error was here
      this.variants = []
      this._originalVariants = []
    }
  }

  onVariantChange (event) {
    // Skip if we're syncing to prevent loops
    if (VariantSelects.syncInProgress) {
      return
    }

    this.updateOptions()

    // Check if Size or Style changed - filter options accordingly
    const changedElement = event.target
    const requiresFiltering = this.isOptionChangeRequiringFiltering(changedElement)

    if (requiresFiltering) {
      this.filterOptions()
    }

    this.updateMasterId()

    this.updateSelectionMetadata(event)
    this.updateVariantStatuses()
    this.setAvailability()

    // Update product-info availability status if needed
    this.updateProductInfoAvailability()

    // Call updatePrices before syncing (so it's not blocked by syncInProgress)
    if (this.currentVariant) {
      // eslint-disable-next-line no-undef
      if (typeof updatePrices === 'function' || typeof window.updatePrices === 'function') {
        // eslint-disable-next-line no-undef
        const priceUpdateFn = typeof updatePrices === 'function' ? updatePrices : window.updatePrices
        priceUpdateFn(this.currentVariant.price, this.currentVariant.compare_at_price, this.currentVariant.available)
      }
    }

    // Sync other variant selects on the page
    this.syncOtherVariantSelects()

    // Publish events for other components - but not during sync
    if (this.currentVariant && !VariantSelects.syncInProgress) {
      // eslint-disable-next-line no-undef
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.dataset.section,
          variant: this.currentVariant
        }
      })

      // product-info.js sends selectedOptionValues as `?option_values=...` to the
      // section-render API, which expects option-value IDs, not value names.
      // `this.options` collects input values (names) for sync/radio matching, so
      // build a parallel array of IDs here.
      const selectedOptionValueIds = []
      this.querySelectorAll('fieldset input[type="radio"]:checked, fieldset select').forEach((el) => {
        if (el.tagName === 'SELECT') {
          const opt = el.selectedOptions && el.selectedOptions[0]
          if (opt && opt.dataset.optionValueId) {
            selectedOptionValueIds.push(opt.dataset.optionValueId)
          }
        } else if (el.dataset.optionValueId) {
          selectedOptionValueIds.push(el.dataset.optionValueId)
        }
      })

      // eslint-disable-next-line no-undef
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target: event.target,
          currentPrice: this.currentVariant.price,
          currentComparePrice: this.currentVariant.compare_at_price,
          currentSelectedValue: this.currentVariant.id,
          selectedOptionValues: selectedOptionValueIds.length ? selectedOptionValueIds : this.options,
          isAvailable: this.currentVariant.available
        }
      })
    }

    if (this.currentVariant) {
      document.dispatchEvent(new CustomEvent('variantChanged', { detail: { variantId: this.currentVariant.id } }))
    }
  }

  syncOtherVariantSelects () {
    // Prevent recursive syncing
    if (VariantSelects.syncInProgress) return
    VariantSelects.syncInProgress = true

    // Find the specific variant-selects we care about (main product and sticky)
    const mainProductSelect = document.querySelector('variant-selects[data-section*="__main"]')
    const stickySelect = document.querySelector('variant-selects[data-section*="__sleepdoctor_sticky_add_to_cart"]')

    // Determine which one to sync to
    let targetSelect = null
    if (this === mainProductSelect && stickySelect) {
      targetSelect = stickySelect
    } else if (this === stickySelect && mainProductSelect) {
      targetSelect = mainProductSelect
    }

    if (!targetSelect) {
      VariantSelects.syncInProgress = false
      return
    }

    // Ensure the target has the same variant data
    targetSelect.variants = this.variants

    // Log what's in the target

    // Update custom selects first - look for Sleep Doctor custom implementation
    const customSelects = targetSelect.querySelectorAll('.selectOptions, .variant_select_options, .selectComponent .selectOptions')
    const hasCustomSelects = customSelects.length > 0

    if (hasCustomSelects) {
      customSelects.forEach((customSelect, index) => {
        if (this.options[index]) {
          // Find the option to select
          const optionToSelect = customSelect.querySelector(`span.selectOption[value="${this.options[index]}"]`)
          if (optionToSelect) {
            // Update the visual state
            customSelect.querySelectorAll('span.selectOption').forEach(opt => {
              opt.removeAttribute('selected')
            })
            optionToSelect.setAttribute('selected', 'selected')

            // Update the displayed value
            const selectedDisplay = customSelect.parentElement.querySelector('span.selected')
            if (selectedDisplay) {
              selectedDisplay.innerHTML = optionToSelect.innerHTML
            }

            // Update the parent div value
            customSelect.parentElement.setAttribute('value', this.options[index])
          }
        }
      })
    }

    // Check for radio buttons (Dawn style)
    const radioFieldsets = targetSelect.querySelectorAll('fieldset')

    if (radioFieldsets.length > 0) {
      radioFieldsets.forEach((fieldset, index) => {
        if (this.options[index]) {
          const radioToSelect = fieldset.querySelector(`input[type="radio"][value="${this.options[index]}"]`)
          if (radioToSelect) {
            radioToSelect.checked = true
            // Don't trigger change during sync
          } else {
            // Radio not found for this value
          }
        }
      })
    }

    // Also check for standard selects
    const allSelects = targetSelect.querySelectorAll('select')

    if (allSelects.length > 0) {
      allSelects.forEach((select, index) => {
        if (this.options[index] && select.value !== this.options[index]) {
          select.value = this.options[index]
        }
      })
    }

    // Update internal state
    targetSelect.options = [...this.options]
    targetSelect.currentVariant = this.currentVariant
    targetSelect.updateVariantInput()
    targetSelect.setAvailability()

    // Important: Update the variant statuses and available options for the target
    targetSelect.updateVariantStatuses()

    // If current variant is not available, update the UI to reflect that
    if (this.currentVariant && !this.currentVariant.available) {
      // Update product form buttons and text
      const targetForm = document.getElementById(`product-form-${targetSelect.dataset.section}`)
      if (targetForm) {
        const addButton = targetForm.querySelector('[name="add"]')
        const addButtonText = targetForm.querySelector('[name="add"] > span')
        if (addButton) {
          addButton.disabled = true
          if (addButtonText) {
            addButtonText.textContent = window.variantStrings?.soldOut || 'Sold out'
          }
        }
      }
    }

    // Reset sync flag after a small delay to ensure all events have processed
    setTimeout(() => {
      VariantSelects.syncInProgress = false

      // After sync is complete, trigger filtering on the target to update options
      if (targetSelect && typeof targetSelect.filterOptions === 'function') {
        targetSelect._filteringTriggeredBySync = true
        targetSelect.filterOptions()
        targetSelect._filteringTriggeredBySync = false
      }
    }, 100)
  }

  updateOptions () {
    const options = []

    // Check for custom select implementation first
    const customSelects = this.querySelectorAll('.selectOptions, .variant_select_options')
    if (customSelects.length > 0) {
      customSelects.forEach(customSelect => {
        const selectedOption = customSelect.querySelector('span[selected]')
        if (selectedOption) {
          options.push(selectedOption.getAttribute('value'))
        }
      })
    }

    // If no custom selects or no selections, use standard selects
    if (options.length === 0) {
      this.querySelectorAll('select').forEach(select => {
        if (select.value) {
          options.push(select.value)
        }
      })
    }

    // Also check radio buttons
    this.querySelectorAll('fieldset').forEach(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked')
      if (checkedInput) {
        options.push(checkedInput.value)
      }
    })

    this.options = options
  }

  updateMasterId () {
    this.currentVariant = this.getVariantData()
    this.updateVariantInput()
  }

  setAvailability () {
    // Update all add to cart buttons on the page
    const buttons = document.querySelectorAll('[name="add"], .product-form__submit')

    buttons.forEach(button => {
      const buttonText = button.querySelector('span') || button

      if (!this.currentVariant) {
        // No valid variant combination
        button.disabled = true
        buttonText.textContent = 'Unavailable'
        return
      }

      if (this.currentVariant.available) {
        // Product is available
        button.disabled = false
        buttonText.textContent = 'Add to Cart'
      } else {
        // Product exists but out of stock
        button.disabled = true
        buttonText.textContent = 'Out of Stock'
      }
    })
  }

  getVariantData () {
    // Ensure variants are loaded
    if (!Array.isArray(this.variants) || this.variants.length === 0) {
      this.loadVariants()
    }

    // Return null if no variants available
    if (!this.variants || this.variants.length === 0) {
      return null
    }

    // Return the variant that matches current option selections
    const matchedVariant = this.variants.find((variant) => {
      // Ensure variant exists
      if (!variant) {
        return false
      }

      if (!this.options || this.options.length === 0) {
        return variant.available
      }

      const matches = this.options.every((option, index) => {
        const variantOption = variant[`option${index + 1}`]
        return variantOption === option
      })

      return matches
    })

    if (!matchedVariant) {
      // No variant matches current options
    }

    return matchedVariant
  }

  updateVariantInput () {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      if (input) {
        const variantId = this.currentVariant?.id || ''
        input.value = variantId

        // Disable add to cart button if no variant
        const addButton = productForm.querySelector('[name="add"]')
        if (addButton) {
          if (!variantId) {
            addButton.disabled = true
            const buttonText = addButton.querySelector('span')
            if (buttonText) {
              buttonText.textContent = 'Unavailable'
            }
          }
        }

        // Only dispatch change event if we're not syncing
        if (!VariantSelects.syncInProgress && variantId) {
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    })
  }

  updateVariantStatuses () {
    // Update which options are available based on current selection
    this.updateAvailableOptions()

    // Handle variant status display
    const variantStatus = this.querySelector('[data-variant-status]')
    if (variantStatus && this.currentVariant) {
      if (!this.currentVariant.available) {
        variantStatus.classList.remove('hidden')
      } else {
        variantStatus.classList.add('hidden')
      }
    }
  }

  updateAvailableOptions () {
    // Get all option positions
    const optionPositions = ['option1', 'option2', 'option3'].filter(opt =>
      this.variants.some(v => v[opt] !== null)
    )

    // Special case: if NO variants are available, disable all options
    const hasAnyAvailableVariants = this.variants.some(v => v.available)

    if (!hasAnyAvailableVariants) {
      optionPositions.forEach((optionKey, optionIndex) => {
        const possibleValues = [...new Set(this.variants.map(v => v[optionKey]))].filter(Boolean)
        possibleValues.forEach(value => {
          this.setOptionAvailability(optionIndex, value, false)
        })
      })
      return
    }

    // For each option position
    optionPositions.forEach((optionKey, optionIndex) => {
      const currentSelections = [...this.options]

      // Find all possible values for this option
      const possibleValues = [...new Set(this.variants.map(v => v[optionKey]))].filter(Boolean)

      possibleValues.forEach(value => {
        // Create a test selection with this value
        const testSelections = [...currentSelections]
        testSelections[optionIndex] = value

        // Check if any variant exists with this combination AND is available
        let hasValidVariant = false

        if (optionIndex === 0) {
          hasValidVariant = this.variants.some(variant =>
            variant[optionPositions[optionIndex]] === value && variant.available
          )
        } else {
          hasValidVariant = this.variants.some(variant => {
            for (let i = 0; i < optionIndex; i++) {
              if (currentSelections[i] && variant[optionPositions[i]] !== currentSelections[i]) {
                return false
              }
            }
            return variant[optionPositions[optionIndex]] === value && variant.available
          })
        }

        // Update UI elements based on availability
        this.setOptionAvailability(optionIndex, value, hasValidVariant)
      })
    })
  }

  setOptionAvailability (optionIndex, value, isAvailable) {
    const fieldset = this.querySelector(`fieldset:nth-of-type(${optionIndex + 1})`)
    if (fieldset) {
      const radio = fieldset.querySelector(`input[value="${value}"]`) ||
                    fieldset.querySelector(`input[value="${value.replace(/"/g, '\\"')}"]`)

      if (radio) {
        radio.disabled = !isAvailable
        const label = radio.nextElementSibling ||
                     fieldset.querySelector(`label[for="${radio.id}"]`) ||
                     radio.parentElement.querySelector('label')

        if (label) {
          label.classList.toggle('unavailable', !isAvailable)
          label.classList.toggle('disabled', !isAvailable)
        }
      }
    }

    // Handle select options - try multiple selectors
    const selects = this.querySelectorAll('select')
    if (selects.length > optionIndex) {
      const selectOption = selects[optionIndex].querySelector(`option[value="${value}"]`)
      if (selectOption) {
        selectOption.disabled = !isAvailable
        if (!isAvailable) {
          // Store original text if not already stored
          if (!selectOption.dataset.originalText) {
            selectOption.dataset.originalText = selectOption.textContent.replace(/ - Unavailable/g, '')
          }
          // Only add " - Unavailable" if it's not already there
          if (!selectOption.textContent.includes(' - Unavailable')) {
            selectOption.textContent = `${selectOption.dataset.originalText} - Unavailable`
          }
        } else {
          // Restore original text
          if (selectOption.dataset.originalText) {
            selectOption.textContent = selectOption.dataset.originalText
            delete selectOption.dataset.originalText
          }
        }
      }
    }

    // Handle custom select options (Sleep Doctor)
    const customSelects = this.querySelectorAll('.selectOptions, .variant_select_options')
    if (customSelects.length > optionIndex) {
      const customOption = customSelects[optionIndex].querySelector(`span[value="${value}"]`)
      if (customOption) {
        customOption.classList.toggle('unavailable', !isAvailable)
        customOption.classList.toggle('disabled', !isAvailable)
        if (!isAvailable) {
          customOption.style.opacity = '0.5'
          customOption.style.pointerEvents = 'none'
          customOption.style.cursor = 'not-allowed'
        } else {
          customOption.style.opacity = ''
          customOption.style.pointerEvents = ''
          customOption.style.cursor = ''
        }
      }
    }
  }

  toggleAddButton (disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`)
    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    const addButtonText = productForm.querySelector('[name="add"] > span')

    if (!addButton) return

    if (disable) {
      addButton.setAttribute('disabled', 'disabled')
      if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      addButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart'
    }

    // modifyClass parameter is kept for compatibility but not used in this implementation
  }

  updateSelectionMetadata ({ target }) {
    const { value, tagName } = target

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options).forEach(option => option.removeAttribute('selected'))
      target.selectedOptions[0].setAttribute('selected', 'selected')

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch')
      if (!selectedDropdownSwatchValue) return
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue)
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable')
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset')
        selectedDropdownSwatchValue.classList.add('swatch--unavailable')
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      )
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const radios = target.closest('fieldset').querySelectorAll('input[type="radio"]')
      radios.forEach(radio => radio.removeAttribute('checked'))
      target.setAttribute('checked', 'checked')

      const selectedSwatchValue = target.closest('.product-form__input').querySelector('[data-selected-value]')
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
    }
  }

  getInputForEventTarget (target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
  }
}

// eslint-disable-next-line no-undef
customElements.define('variant-selects', VariantSelects)

// eslint-disable-next-line no-undef
class ProductRecommendations extends HTMLElement {
  observer = undefined

  connectedCallback () {
    this.initializeRecommendations(this.dataset.productId)
  }

  initializeRecommendations (productId) {
    this.observer?.unobserve(this)
    // eslint-disable-next-line no-undef
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return
        observer.unobserve(this)
        this.loadRecommendations(productId)
      },
      { rootMargin: '0px 0px 400px 0px' }
    )
    this.observer.observe(this)
  }

  loadRecommendations (productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div')
        html.innerHTML = text
        const recommendations = html.querySelector('product-recommendations')

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove()
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded')
        }
      })
      .catch((e) => {
        // Error: Console error was here
      })
  }
}

// eslint-disable-next-line no-undef
customElements.define('product-recommendations', ProductRecommendations)

// eslint-disable-next-line no-undef
class AccountIcon extends HTMLElement {
  constructor () {
    super()

    this.icon = this.querySelector('.icon')
  }

  connectedCallback () {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this))
  }

  handleStorefrontSignInCompleted (event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode())
    }
  }
}

// eslint-disable-next-line no-undef
customElements.define('account-icon', AccountIcon)

// eslint-disable-next-line no-undef
class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250

  constructor () {
    super()
    this.queue = []
    this.setRequestStarted(false)
    this.ids = []
  }

  startQueue (id, quantity) {
    this.queue.push({ id, quantity })

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue)
        }
      } else {
        clearInterval(interval)
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY)
  }

  sendRequest (queue) {
    this.setRequestStarted(true)
    const items = {}

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity
    })
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement))

    this.updateMultipleQty(items)
  }

  setRequestStarted (requestStarted) {
    this._requestStarted = requestStarted
  }

  get requestStarted () {
    return this._requestStarted
  }

  resetQuantityInput (id) {
    const input = this.querySelector(`#Quantity-${id}`)
    input.value = input.getAttribute('value')
    this.isEnterPressed = false
  }

  setValidity (event, index, message) {
    event.target.setCustomValidity(message)
    event.target.reportValidity()
    this.resetQuantityInput(index)
    event.target.select()
  }

  validateQuantity (event) {
    const inputValue = parseInt(event.target.value)
    const index = event.target.dataset.index

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min))
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max))
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step))
    } else {
      event.target.setCustomValidity('')
      event.target.reportValidity()
      event.target.setAttribute('value', inputValue)
      this.startQueue(index, inputValue)
    }
  }

  getSectionInnerHTML (html, selector) {
    return new window.DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
  }
}

// eslint-disable-next-line no-undef
if (!customElements.get('bulk-add')) {
  // eslint-disable-next-line no-undef
  customElements.define('bulk-add', BulkAdd)
}

// eslint-disable-next-line no-unused-vars
class CartPerformance {
  static #metric_prefix = 'cart-performance'

  static createStartingMarker (benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`)
  }

  static measureFromEvent (benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    // eslint-disable-next-line no-unused-vars
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    })

    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`)

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    )
  }

  static measureFromMarker (benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`)

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    )
  }

  static measure (benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    // eslint-disable-next-line no-unused-vars
    const startMarker = performance.mark(`${metricName}:start`)

    callback()

    // eslint-disable-next-line no-unused-vars
    const endMarker = performance.mark(`${metricName}:end`)

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    )
  }
}
